import { useMemo } from "react";

import { ColumnSortState } from "../../stores/slices/columnSortSlice";
import { useColumnSortState } from "./useColumnSortState";

type Entry = Record<string, unknown>;

interface ColumnSortFuncHook {
  <Key extends keyof ColumnSortState>(
    table: Key,
    options: {
      dateFields: Set<string>;
      numericFields: Set<string>;
    }
  ): (a: Entry, b: Entry) => number;
}

export const useColumnSortFunc: ColumnSortFuncHook = (
  table,
  { dateFields, numericFields }
) => {
  const sort = useColumnSortState(table);

  const func = useMemo(
    () =>
      (a: Entry, b: Entry): number => {
        const fieldA = a[sort.field];
        const fieldB = b[sort.field];

        if (numericFields.has(sort.field)) {
          if (sort.order === "asc") {
            return Number(fieldA) - Number(fieldB);
          } else {
            return Number(fieldB) - Number(fieldA);
          }
        }

        if (dateFields.has(sort.field)) {
          const dateA = new Date(String(fieldA));
          const dateB = new Date(String(fieldB));

          if (sort.order === "asc") {
            return dateA.getTime() - dateB.getTime();
          } else {
            return dateB.getTime() - dateA.getTime();
          }
        }

        if (sort.order === "asc") {
          return String(fieldA).localeCompare(String(fieldB));
        }
        return String(fieldB).localeCompare(String(fieldA));
      },
    [table, numericFields, sort.field, sort.order]
  );

  return func;
};
